import type { ScrollAxisName } from "./scroll-box-axes"
import {
  SCROLL_BOX_FINDING_LABELS,
  findingAxes,
  type ScrollBoxFinding,
  type ScrollBoxFindingKind,
} from "./scroll-box-findings"
import type { AxisVerdict, ScrollBoxUnitKind, ScrollBoxVerdict } from "./scroll-box-verdict"
import type { ScrollBoxReport } from "./scroll-box-watcher"

const SENTRY_FINGERPRINT_PREFIX = "scroll-box"
const TAG_LIST_SEPARATOR = ","

type ScrollBoxSentryTags = {
  "scroll_box.kind": ScrollBoxFindingKind
  "scroll_box.route": string
  "scroll_box.axes": string
  "scroll_box.units": string
}

type AxisSentryContext = {
  unit_kind: ScrollBoxUnitKind
  units_in_content: number
  whole_units_that_fit: number
  whole_units_required: number
  median_unit_px: number
  effective_px: number
  overflow_px: number
  sticky_start_px: number
  sticky_end_px: number
  is_too_small: boolean
}

type ScrollBoxSentryContext = {
  kind: ScrollBoxFindingKind
  key: string
  source_file: string | null
  route: string
  box_width_px: number
  box_height_px: number
  window_width_px: number
  window_height_px: number
  device_pixel_ratio: number
  checks_skipped_below_minimum_window: number
}

type FindingDetailSentryContext = {
  axis: ScrollAxisName
  edge: string | null
  cause: string | null
  related_key: string | null
  measured_px: number
  unit_px: number | null
}

type ScrollBoxSentryContexts = {
  scroll_box: ScrollBoxSentryContext
  scroll_box_vertical: AxisSentryContext | undefined
  scroll_box_horizontal: AxisSentryContext | undefined
  scroll_box_finding: FindingDetailSentryContext | undefined
}

export type ScrollBoxCaptureContext = {
  level: "warning"
  fingerprint: string[]
  tags: ScrollBoxSentryTags
  contexts: ScrollBoxSentryContexts
}

export type ScrollBoxSentryEvent = {
  message: string
  captureContext: ScrollBoxCaptureContext
}

function axisSentryContext(verdict: ScrollBoxVerdict, axis: ScrollAxisName): AxisSentryContext | undefined {
  const axisVerdict: AxisVerdict | undefined = verdict.overflowingAxes.find((candidate) => candidate.axis === axis)
  return axisVerdict && {
    unit_kind: axisVerdict.unitKind,
    units_in_content: axisVerdict.unitsInContent,
    whole_units_that_fit: axisVerdict.wholeUnitsThatFit,
    whole_units_required: axisVerdict.wholeUnitsRequired,
    median_unit_px: axisVerdict.medianUnitPx,
    effective_px: axisVerdict.effectivePx,
    overflow_px: axisVerdict.overflowPx,
    sticky_start_px: axisVerdict.stickyStartPx,
    sticky_end_px: axisVerdict.stickyEndPx,
    is_too_small: axisVerdict.isTooSmall,
  }
}

function findingDetailContext(finding: ScrollBoxFinding): FindingDetailSentryContext | undefined {
  switch (finding.kind) {
    case "too-small":
      return undefined
    case "cut-off-content":
      return { axis: finding.axis, edge: null, cause: null, related_key: null, measured_px: finding.cutOffPx, unit_px: null }
    case "outside-window":
      return {
        axis: finding.axis,
        edge: finding.edge,
        cause: finding.reason,
        related_key: null,
        measured_px: finding.outsidePx,
        unit_px: null,
      }
    case "hidden-end":
      return {
        axis: finding.axis,
        edge: finding.edge,
        cause: finding.cause,
        related_key: null,
        measured_px: finding.hiddenPx,
        unit_px: null,
      }
    case "cut-popup":
      return {
        axis: finding.axis,
        edge: null,
        cause: null,
        related_key: finding.popupKey,
        measured_px: finding.cutOffPx,
        unit_px: null,
      }
    case "unused-room":
      return {
        axis: finding.axis,
        edge: null,
        cause: null,
        related_key: finding.frameKey,
        measured_px: finding.unusedRoomPx,
        unit_px: finding.medianUnitPx,
      }
  }
}

function tooSmallUnitKinds(finding: ScrollBoxFinding): string {
  if (finding.kind !== "too-small") return ""
  return finding.verdict.overflowingAxes
    .filter((axisVerdict) => axisVerdict.isTooSmall)
    .map((axisVerdict) => axisVerdict.unitKind)
    .join(TAG_LIST_SEPARATOR)
}

function verdictAxisContext(finding: ScrollBoxFinding, axis: ScrollAxisName): AxisSentryContext | undefined {
  return finding.kind === "too-small" ? axisSentryContext(finding.verdict, axis) : undefined
}

export function scrollBoxSentryEvent(report: ScrollBoxReport): ScrollBoxSentryEvent {
  const { finding } = report
  return {
    message: `${SCROLL_BOX_FINDING_LABELS[finding.kind]}: ${finding.identity.key}`,
    captureContext: {
      level: "warning",
      fingerprint: [SENTRY_FINGERPRINT_PREFIX, finding.kind, report.route, finding.identity.key],
      tags: {
        "scroll_box.kind": finding.kind,
        "scroll_box.route": report.route,
        "scroll_box.axes": findingAxes(finding).join(TAG_LIST_SEPARATOR),
        "scroll_box.units": tooSmallUnitKinds(finding),
      },
      contexts: {
        scroll_box: {
          kind: finding.kind,
          key: finding.identity.key,
          source_file: finding.identity.sourceFile,
          route: report.route,
          box_width_px: finding.boxWidthPx,
          box_height_px: finding.boxHeightPx,
          window_width_px: report.windowWidthPx,
          window_height_px: report.windowHeightPx,
          device_pixel_ratio: report.devicePixelRatio,
          checks_skipped_below_minimum_window: report.checksSkippedBelowMinimumWindow,
        },
        scroll_box_vertical: verdictAxisContext(finding, "vertical"),
        scroll_box_horizontal: verdictAxisContext(finding, "horizontal"),
        scroll_box_finding: findingDetailContext(finding),
      },
    },
  }
}
